<template>
  <v-card class="markup-list" flat>
    <!-- Approval Section -->
    <div v-if="canApprove" class="approval-section pa-4">
      <v-card-title class="d-flex align-center mb-3 pa-0">
        <v-divider
          class="custom-vertical-divider ml-1 mr-2"
          vertical
        ></v-divider>

        <v-icon left>mdi-list-status</v-icon>
        <span class="font-weight-bold">{{ $t('markup.approval') }}</span>
        <v-spacer></v-spacer>
      </v-card-title>

      <v-select
        v-model="selectedStatus"
        :items="statusOptions"
        item-text="label"
        item-value="value"
        outlined
        dense
        hide-details
        :disabled="updatingStatus"
        :loading="updatingStatus"
        @change="onStatusChange"
      ></v-select>
    </div>

    <v-divider v-if="canApprove"></v-divider>

    <!-- Markups Section Header -->
    <v-card-title class="d-flex align-center px-4 py-3">
      <v-divider
        class="custom-vertical-divider ml-1 mr-2"
        vertical
      ></v-divider>
      <v-icon left>mdi-message-outline</v-icon>
      <span class="font-weight-bold">{{ $t('markup.markups') }}</span>
    </v-card-title>

    <v-divider></v-divider>

    <!-- Markups List -->
    <v-list class="markup-list-items" two-line>
      <template v-if="markups.length === 0">
        <v-list-item>
          <v-list-item-content>
            <v-list-item-subtitle class="text-center py-8 text--secondary">
              {{ $t('markup.no_markups_yet') }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </template>

      <template v-else>
        <div
          v-for="(markup, index) in sortedMarkups"
          :key="markup.markup_id"
          class="markup-item-wrapper"
        >
          <div
            class="markup-card"
            :class="{ 'markup-selected': selectedMarkupId === markup.markup_id }"
          >
            <v-list-item
              @click="handleMarkupClick(markup)"
              class="markup-item-main"
            >
              <v-list-item-content class="pa-0">
                <div class="d-flex">
                  <v-avatar :color="getMarkupColor(markup)" size="36" class="mr-3">
                    <span class="white--text font-weight-bold">{{ index + 1 }}</span>
                  </v-avatar>

                  <div class="flex-grow-1">
                    <div class="d-flex align-center mb-0">
                      <span class="font-weight-bold">{{ getUserName(markup.users_id, markup.user_email) }}</span>
                      <v-spacer></v-spacer>
                      <v-chip
                        v-if="markup.page_number"
                        small
                        color="info lighten-1"
                        dark
                      >
                        <v-icon small left>mdi-file-document</v-icon>
                        {{ $t('markup.page') }} {{ markup.page_number }}
                      </v-chip>
                      <v-chip
                        v-else-if="markup.timestamp_video !== null && markup.timestamp_video !== undefined"
                        small
                        color="info lighten-1"
                        dark
                      >
                        <v-icon small left>mdi-clock-outline</v-icon>
                        {{ formatTimestamp(markup.timestamp_video) }}
                      </v-chip>
                    </div>
                    <div class="text--secondary text-caption mb-2">
                      {{ formatDate(markup.created_at) }} • {{ formatTime(markup.created_at) }}
                    </div>
                  </div>
                </div>

                <div
                  class="text--primary message-preview"
                  @click.stop
                  @mousedown.stop
                >
                  {{ markup.message }}
                </div>

                <!-- Action Buttons at Bottom -->
                <div class="markup-actions-row mt-3 d-flex align-center">
                  <v-btn
                    text
                    @click.stop="toggleChat(markup)"
                    class="text-none"
                  >
                    <v-icon left>mdi-reply</v-icon>
                    {{ $t('markup.reply') }}
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-select
                    :value="markup.status"
                    :items="markupStatusOptions"
                    item-text="label"
                    item-value="value"
                    outlined
                    dense
                    hide-details
                    class="markup-status-select mr-2"
                    :disabled="!canEdit || updatingMarkupStatusId === markup.markup_id"
                    :loading="updatingMarkupStatusId === markup.markup_id"
                    @click.stop
                    @mousedown.stop
                    @change="onMarkupStatusChange(markup, $event)"
                  ></v-select>
                  <v-btn
                    icon
                    @click.stop="$emit('delete-markup', markup)"
                    small
                  >
                    <v-icon small>mdi-delete-outline</v-icon>
                  </v-btn>
                </div>
              </v-list-item-content>
            </v-list-item>

            <!-- Expanded Chat Section -->
            <v-expand-transition>
              <div v-if="expandedMarkupId === markup.markup_id" class="chat-expansion">
                <v-divider></v-divider>

              <!-- Chat Messages -->
              <div class="chat-messages-list pa-3">
                <div v-if="loadingChat" class="text-center pa-4">
                  <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
                </div>

                <template v-else-if="chatMessages.length > 0">
                  <div
                    v-for="message in chatMessages"
                    :key="message.markup_chat_id"
                    class="chat-message mb-3"
                  >
                    <div class="d-flex align-start">
                      <div class="flex-grow-1">
                        <div class="font-weight-bold">
                          {{ getUserName(message.users_id, message.user_email) }}
                        </div>
                        <div class="text-caption text--secondary mb-1">
                          {{ formatDateTime(message.created_at) }}
                        </div>
                        <div class="message-text">
                          {{ message.message }}
                        </div>
                      </div>
                    </div>
                  </div>
                </template>

                <div v-else class="text-center py-4 text--secondary">
                  {{ $t('markup.no_messages_yet') }}
                </div>
              </div>

              <v-divider></v-divider>

              <!-- Reply Input -->
              <div class="pa-3">
                <v-text-field
                  v-model="replyMessage"
                  :placeholder="$t('markup.reply')"
                  outlined
                  dense
                  hide-details
                  @keyup.enter="sendReply(markup)"
                  :disabled="sendingReply"
                >
                  <template v-slot:append>
                    <v-btn
                      icon
                      small
                      color="primary"
                      @click="sendReply(markup)"
                      :disabled="!replyMessage || sendingReply"
                      :loading="sendingReply"
                    >
                      <v-icon small>mdi-send</v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
              </div>
            </div>
          </v-expand-transition>
          </div>
        </div>
      </template>
    </v-list>
  </v-card>
</template>

<script>
import api from '@/services/api'

const TIMEZONE_SUFFIX_PATTERN = /(Z|[+-]\d{2}:?\d{2})$/i

function parseUtcDate(dateString) {
  const normalizedDate = dateString.replace(' ', 'T')
  return new Date(TIMEZONE_SUFFIX_PATTERN.test(normalizedDate) ? normalizedDate : `${normalizedDate}Z`)
}

export default {
  name: 'MarkupList',

  props: {
    markups: {
      type: Array,
      default: () => []
    },
    users: {
      type: Array,
      default: () => []
    },
    selectedMarkupId: {
      type: Number,
      default: null
    },
    approvalStatus: {
      type: String,
      default: 'pending'
    },
    canApprove: {
      type: Boolean,
      default: true
    },
    canEdit: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      expandedMarkupId: null,
      chatMessages: [],
      loadingChat: false,
      replyMessage: '',
      sendingReply: false,
      updatingStatus: false,
      selectedStatus: this.approvalStatus,
      updatingMarkupStatusId: null
    }
  },

  watch: {
    approvalStatus(newValue) {
      this.selectedStatus = newValue
    }
  },

  computed: {
    sortedMarkups() {
      return [...this.markups].sort((a, b) => {
        return parseUtcDate(a.created_at) - parseUtcDate(b.created_at)
      })
    },

    statusOptions() {
      return [
        { label: '', value: 'no_status' },
        { label: this.$t('markup.status_option_pending'), value: 'pending' },
        { label: this.$t('markup.status_option_approved'), value: 'approved' },
        { label: this.$t('markup.status_option_rejected'), value: 'rejected' },
        { label: this.$t('markup.status_option_approved_finalize'), value: 'approved_finalize' }
      ]
    },

    markupStatusOptions() {
      return [
        { label: this.$t('markup.markup_status_option_requested'), value: 'pending' },
        { label: this.$t('markup.markup_status_option_fixed'), value: 'fixed' },
        { label: this.$t('markup.markup_status_option_approved'), value: 'approved' },
        { label: this.$t('markup.markup_status_option_rejected'), value: 'rejected' }
      ]
    },

    statusColor() {
      const colors = {
        pending: 'orange',
        approved: 'success',
        rejected: 'error',
        cancelled: 'grey'
      }
      return colors[this.approvalStatus] || 'grey'
    },

    statusText() {
      const texts = {
        pending: this.$t('markup.status_pending'),
        approved: this.$t('markup.status_approved'),
        rejected: this.$t('markup.status_rejected'),
        cancelled: this.$t('markup.status_cancelled')
      }
      return texts[this.approvalStatus] || this.approvalStatus
    }
  },

  methods: {
    onStatusChange(value) {
      this.$emit('status-change', value)
    },

    onMarkupStatusChange(markup, value) {
      this.$emit('markup-status-change', { markup, status: value })
    },

    markupStatusColor(status) {
      const colors = {
        pending: 'blue-grey',
        approved: 'success',
        cancelled: 'grey'
      }
      return colors[status] || 'grey'
    },

    markupStatusText(status) {
      const texts = {
        pending: this.$t('status_type_waiting'),
        approved: this.$t('status_type_done'),
        cancelled: this.$t('status_type_canceled')
      }
      return texts[status] || status
    },

    formatDate(dateString) {
      const date = parseUtcDate(dateString)
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    },

    formatTime(dateString) {
      const date = parseUtcDate(dateString)
      return date.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    formatDateTime(dateString) {
      const date = parseUtcDate(dateString)
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    formatTimestamp(seconds) {
      const minutes = Math.floor(seconds / 60)
      const secs = Math.floor(seconds % 60)
      return `${minutes}:${secs.toString().padStart(2, '0')}`
    },

    handleMarkupClick(markup) {
      this.$emit('select-markup', markup)
    },

    getUserName(userId, userEmail) {
      if (userEmail && userEmail.trim()) {
        return userEmail
      }

      const user = this.users.find(u => u.users_id === userId)

      return user ? user.name : 'Usuário'
    },

    getMarkupColor(markup) {
      // Use the color from the database directly
      return markup.color || 'primary'
    },

    async toggleChat(markup) {
      if (this.expandedMarkupId === markup.markup_id) {
        // Close the chat
        this.expandedMarkupId = null
        this.chatMessages = []
      } else {
        // Open the chat
        this.expandedMarkupId = markup.markup_id
        this.loadingChat = true

        try {
          const response = await api.content.markup.getMarkupChatByMarkup(markup.markup_id)
          this.chatMessages = response.data.items
        } catch (error) {
          console.error('Error loading chat:', error)
          this.$store.dispatch('app/showSnackbar', {
            text: 'Erro ao carregar chat',
            color: 'error'
          })
        } finally {
          this.loadingChat = false
        }
      }
    },

    async sendReply(markup) {
      if (!this.replyMessage) return

      this.sendingReply = true
      try {
        const response = await api.content.markup.createMarkupChat({
          markup_id: markup.markup_id,
          message: this.replyMessage
        })

        this.chatMessages.push(response.data)
        this.replyMessage = ''

        // Update chat count on the markup
        markup.chat_count = (markup.chat_count || 0) + 1

        this.$store.dispatch('app/showSnackbar', {
          text: 'Mensagem enviada',
          color: 'success'
        })
      } catch (error) {
        console.error('Error sending reply:', error)
        this.$store.dispatch('app/showSnackbar', {
          text: 'Erro ao enviar mensagem',
          color: 'error'
        })
      } finally {
        this.sendingReply = false
      }
    }
  }
}
</script>

<style scoped>
.markup-list {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.markup-list-items {
  flex: 1;
  overflow-y: auto;
  max-height: calc(100vh - 300px);
  padding: 0 !important;
  background-color: rgb(240, 240, 240) !important;
}

.markup-item-wrapper {
  position: relative;
  margin: 12px 16px;
}

.markup-card {
  background-color: white;
  border-radius: 8px;
  transition: all 0.2s;
  overflow: hidden;
}

.markup-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  border-color: rgba(33, 150, 243, 0.3);
}

.markup-card.markup-selected {
  border: 2px solid #2196F3;
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.2) !important;
}

.markup-item-main {
  padding: 16px !important;
  cursor: pointer;
  background-color: transparent !important;
}

.markup-item-main::before {
  display: none !important;
}

.markup-actions {
  flex-direction: row;
  align-items: center;
  margin: 0;
}

.chat-expansion {
  background-color: white;
}

.chat-messages-list {
  max-height: 300px;
  overflow-y: auto;
}

.chat-message {
  padding: 8px 12px;
  background-color: #f9f9f9;
  border-radius: 8px;
  border-left: 3px solid #2196F3;
}

.message-text {
  margin-top: 4px;
  color: rgba(0, 0, 0, 0.87);
  white-space: pre-wrap;
  word-wrap: break-word;
}

.markup-item-main {
  align-items: flex-start !important;
  min-height: 120px;
}

.markup-item-main >>> .v-list-item__avatar {
  align-self: flex-start !important;
  margin-top: 0 !important;
}

.message-preview {
  white-space: normal;
  word-wrap: break-word;
  line-height: 1.4;
  cursor: text;
  user-select: text;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
}

/* Allow selecting the chat message text too */
.message-text {
  cursor: text;
  user-select: text;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
}

.markup-actions-row {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.approval-section {
  background-color: white;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

.markup-status-select {
  max-width: 220px;
  flex: 0 0 auto;
}

.custom-vertical-divider {
  border-width: 0 medium 0 0 !important;
  border-color: var(--v-primary-base);
}
</style>
